import type { CustomClaims, RMUser } from "../webapp/src/types";
import { isRMCertified } from "./rmCertification";

const SEGMENT_CLAIMS = {
    kfh_rm_private_banking: "Private Banking",
    kfh_rm_ruwad: "Ruwad",
    kfh_rm_tamayoz: "Tamayoz",
} as const;

export function hasKfhRMClaims(claims: CustomClaims): boolean {
    return ["kfh_rm", "kfh_oper", "kfh_rm_certified", "kfh_rm_approver", ...Object.keys(SEGMENT_CLAIMS)]
        .some(key => (claims as Record<string, unknown>)[key] === true);
}

const segmentsFrom = (value: unknown): string[] => Array.from(new Set(
    (Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [])
        .filter((segment): segment is string => typeof segment === "string")
        .map(segment => segment.trim()).filter(Boolean),
));

export interface KfhRMAccess {
    eligible: boolean;
    email: string;
    rmId: string;
    segment?: string;
    segments: string[];
    reviewSegments: string[];
    operations: boolean;
    certified: boolean;
    approver: boolean;
}

/** Directory segment claims take precedence; legacy profile scope remains the fallback. */
export function resolveKfhRMAccess(claims: CustomClaims, rm: Partial<RMUser> | undefined, loginEmail: string): KfhRMAccess {
    const claimedSegments = Object.entries(SEGMENT_CLAIMS)
        .filter(([key]) => (claims as Record<string, unknown>)[key] === true)
        .map(([, segment]) => segment);
    const legacySegment = rm?.segment?.trim() || claims.segment?.trim();
    const segment = claimedSegments.length
        ? claimedSegments.find(value => value === legacySegment) || claimedSegments[0]
        : legacySegment;
    const legacySegments = segmentsFrom(rm?.availableSegments ?? claims.availableSegments);
    const segments = claimedSegments.length ? claimedSegments : legacySegments.length ? legacySegments : segment ? [segment] : [];
    const email = (rm?.email || loginEmail).trim();
    const rmId = `${rm?.rm_id ?? ""}`.trim() || /^([0-9]+)@kfh\.com$/i.exec(loginEmail.trim())?.[1] || "";
    return {
        // Same test as the "RM Not Active" gate in customClaims, so a profile that signs in also gets scope.
        eligible: !!email && (rm ? !!rm.active : hasKfhRMClaims(claims)),
        email,
        rmId,
        segment,
        segments,
        reviewSegments: claimedSegments.length ? claimedSegments : segment ? [segment] : [],
        operations: claims.kfh_oper !== undefined ? claims.kfh_oper === true : rm?.role === "operations",
        certified: isRMCertified(claims, rm),
        approver: claims.kfh_rm_approver === true,
    };
}

export interface KfhOrderConstraint {
    field: string;
    operator: "==" | "in";
    value: string | string[];
}

/** These same branches drive the Typesense key and the Firestore order-list fallback. */
export function kfhOrderVisibilityBranches(access: KfhRMAccess, inLimit = 10): KfhOrderConstraint[][] {
    if (!access.eligible) return [];
    if (access.operations && access.segments.length) {
        const branches: KfhOrderConstraint[][] = [];
        for (let start = 0; start < access.segments.length; start += inLimit) {
            branches.push([{ field: "rm.segment", operator: "in", value: access.segments.slice(start, start + inLimit) }]);
        }
        return branches;
    }
    const branches: KfhOrderConstraint[][] = ["rm.email", "rm.order_handler_email", "referredBy.email", "submittedBy.email"]
        .map(field => [{ field, operator: "==", value: access.email }]);
    if (access.rmId) branches.push([{ field: "rm.rm_id", operator: "==", value: access.rmId }]);
    if (!access.operations) {
        for (const segment of access.reviewSegments) {
            const statuses = [
                ...(access.certified ? ["waiting_certified_rm"] : []),
                ...(access.approver ? ["selling_agent_review", "selling_agent_update"] : []),
            ];
            for (const status of statuses) branches.push([
                { field: "tags.orderStatus.code", operator: "==", value: status },
                { field: "rm.segment", operator: "==", value: segment },
            ]);
        }
    }
    return branches;
}

const quote = (value: string): string => `\`${value.replace(/\\/g, "\\\\").replace(/`/g, "\\`")}\``;
const segmentFilter = (field: string, segments: string[]): string => `${field}:=[${segments.map(quote).join(",")}]`;
export function kfhRMSearchFilters(access: KfhRMAccess): { orders: string; customers: string; services: string; campaigns: string } | undefined {
    if (!access.eligible) return undefined;
    const orders = kfhOrderVisibilityBranches(access).map(branch => {
        const filter = branch.map(constraint => constraint.operator === "in"
            ? segmentFilter(constraint.field, constraint.value as string[])
            : `${constraint.field}:=${quote(constraint.value as string)}`).join(" && ");
        return branch.length > 1 ? `(${filter})` : filter;
    }).join(" || ");
    return {
        orders,
        customers: access.segments.length ? segmentFilter("segment", access.segments) : `rm_email:=${quote(access.email)}`,
        services: access.operations && access.segments.length
            ? `${segmentFilter("customerObj.segment", access.segments)} || ${segmentFilter("referredBy.segment", access.segments)}`
            : access.operations
                ? `customerObj.rm_email:=${quote(access.email)} || rm.email:=${quote(access.email)}`
                : `customerObj.rm_email:=${quote(access.email)} || referredBy.email:=${quote(access.email)}`,
        campaigns: access.operations && access.segments.length
            ? segmentFilter("rm.segment", access.segments)
            : `rm.email:=${quote(access.email)}${!access.operations && access.rmId ? ` || rm.rm_id:=${quote(access.rmId)}` : ""}`,
    };
}
